import { z } from 'zod';
import { OFFER_CHAIN_IDS, offerChain } from './chains.js';
/** Meny, ve kterych se nakupuje. Obchody v Cesku maji CZK. */
export const CurrencySchema = z.enum(['PLN', 'EUR', 'CZK']);
/** Typ prodejny u retezcu se zvlastnim letakem pro supermarkety (Tesco, Albert). */
export const StoreFormatSchema = z.enum(['hypermarket', 'supermarket']);
/**
 * Obchod patri skupine. Poradi (`position`) je trasa velkeho nakupu —
 * Biedronka v Bogatynii se projizdi pred Kauflandem v Zittau.
 */
export const StoreSchema = z.object({
    id: z.uuid(),
    groupId: z.uuid(),
    name: z.string().min(1).max(80),
    city: z.string().max(80).nullable(),
    currency: CurrencySchema,
    position: z.number().int().nonnegative(),
    archived: z.boolean(),
    /** Retezec, jehoz akce z letaku se u obchodu ukazuji (chains.ts). null = bez akci. */
    offerChain: z.string().nullable(),
    /** PSC pro nemecke akce (marktguru je ma po regionech), u Kauflandu CZ cislo prodejny. */
    offerZip: z.string().nullable(),
    /**
     * Pult s obsluhou („lada tradycyjna"). Biedronka vydava hlavni letak ve verzi
     * pro obchody s pultem a bez nej; null = nevime, ukazou se akce obou verzi.
     * Default kvuli obchodum ulozenym v telefonu starsi verzi.
     */
    offerCounter: z.boolean().nullable().default(null),
    /** Hypermarket nebo supermarket (Tesco, Albert); null = nevime, ukazou se akce obou letaku. */
    offerFormat: StoreFormatSchema.nullable().default(null),
    createdAt: z.iso.datetime(),
});
const storeName = z.string().trim().min(1).max(80);
const storeCity = z.string().trim().max(80).nullable();
const offerChainId = z.string().refine((id) => OFFER_CHAIN_IDS.includes(id), 'Neznámý obchodní řetězec');
/** PSC pro nemecke akce, nebo cislo prodejny Kauflandu (CZ3150, DE2950). */
const offerZip = z
    .string()
    .trim()
    .regex(/^(\d{5}|CZ\d{4}|DE\d{4})$/, 'PSČ má pět číslic');
export const StoreInputSchema = z.object({
    name: storeName,
    city: storeCity.default(null),
    currency: CurrencySchema,
    /** Bez udaje server retezec a PSC odhadne z nazvu, meny a mesta. */
    offerChain: offerChainId.nullable().optional(),
    offerZip: offerZip.nullable().optional(),
    offerCounter: z.boolean().nullable().optional(),
    offerFormat: StoreFormatSchema.nullable().optional(),
});
/** Bez defaultu — viz komentar u CatalogItemPatchSchema. */
export const StorePatchSchema = z.object({
    name: storeName.optional(),
    city: storeCity.optional(),
    currency: CurrencySchema.optional(),
    archived: z.boolean().optional(),
    offerChain: offerChainId.nullable().optional(),
    offerZip: offerZip.nullable().optional(),
    offerCounter: z.boolean().nullable().optional(),
    offerFormat: StoreFormatSchema.nullable().optional(),
});
/** Nove poradi obchodu skupiny; musi obsahovat vsechny jeji nearchivovane obchody. */
export const StoreOrderSchema = z.object({
    storeIds: z.array(z.uuid()).min(1).max(100),
});
export const StoresResponseSchema = z.object({ stores: z.array(StoreSchema) });
/**
 * Navrhy pri pridavani obchodu, aby se Biedronka a Kaufland nemusely psat
 * rucne. Nejsou to data — skupina si z nich jen vybira.
 */
export const STORE_SUGGESTIONS = [
    { name: 'Biedronka Bogatynia', city: 'Bogatynia', currency: 'PLN' },
    { name: 'Kaufland Zittau', city: 'Zittau', currency: 'EUR' },
    { name: 'Netto', city: null, currency: 'EUR' },
];
/** Obchody v poradi trasy, archivovane vynechane. */
export function routeStores(stores) {
    return stores
        .filter((s) => !s.archived)
        .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, 'cs'));
}
/** Zeme obchodu mimo Cesko podle retezce, bez retezce aspon podle zlotych; null = Cesko nebo nevime. */
export function storeCountry(store) {
    const country = offerChain(store.offerChain)?.country;
    if (country === 'DE' || country === 'PL')
        return country;
    if (country === 'CZ')
        return null;
    return store.currency === 'PLN' ? 'PL' : null;
}
/**
 * Obchody podle mesta pro vyber trasy. Mesta v poradi trasy skupiny (podle
 * prvniho obchodu), obchody bez mesta na konci. Mesto se porovnava bez
 * diakritiky a velikosti pismen, zapis se bere z prvniho obchodu.
 */
export function groupStoresByCity(stores) {
    const groups = new Map();
    const noCity = { city: null, country: null, stores: [] };
    const sorted = [...stores].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, 'cs'));
    for (const store of sorted) {
        const city = store.city?.trim() || null;
        if (city === null) {
            noCity.stores.push(store);
            continue;
        }
        const key = city.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('cs-CZ');
        let group = groups.get(key);
        if (!group) {
            group = { city, country: null, stores: [] };
            groups.set(key, group);
        }
        group.stores.push(store);
        group.country ??= storeCountry(store);
    }
    return noCity.stores.length > 0 ? [...groups.values(), noCity] : [...groups.values()];
}
//# sourceMappingURL=stores.js.map